/*
 ARI menu-taxonomy — DATA-DRIVEN navigation metadata (NOT synthetic company data).
 New modules from future reports are added here via configuration; no app redesign needed.
 Each flattened entry carries: menu_id, parent_menu_id, label, business_domain,
 industry_applicability, source_tables, availability_rule, display_order.
*/

export const AVAILABILITY = {
  AVAILABLE: { label: "Available", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" },
  PARTIAL: { label: "Partial", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800 ring-amber-600/20" },
  AWAITING_GOVERNED_SOURCE: { label: "Awaiting source", dot: "bg-slate-400", chip: "bg-slate-100 text-slate-600 ring-slate-500/20" },
  NOT_APPLICABLE: { label: "Not applicable", dot: "bg-stone-300", chip: "bg-stone-100 text-stone-500 ring-stone-400/20" },
};

// availability_rule → concrete state (before industry gating)
const RULE_STATE = {
  ARI_PRESENT: "AVAILABLE",
  ARI_PARTIAL: "PARTIAL",
  ARI_SOURCE: "AWAITING_GOVERNED_SOURCE",
  MARKET_DATA: "AWAITING_GOVERNED_SOURCE",
};

// representative feed tables by domain (truthful: ari.* exist now; ari_source.* pending)
const DOMAIN_TABLES = {
  company: ["ari_source.company_master", "ari_source.company_identifiers", "ari_source.addresses", "ari_source.company_addresses"],
  financials: ["ari.statement_instances", "ari.source_rows", "ari_source.financial_statements", "ari_source.statement_values"],
  ratios: ["ari_source.financial_statements", "ari_source.statement_values"],
  working_results: ["ari_source.financial_statements", "ari_source.statement_columns"],
  governance: ["ari_source.people_master", "ari_source.company_person_roles", "ari_source.board_committees", "ari_source.committee_memberships"],
  audit: ["ari_source.auditors", "ari_source.report_auditors", "ari_source.audit_opinions", "ari_source.key_audit_matters"],
  ownership: ["ari_source.shareholding_snapshots", "ari_source.shareholders", "ari_source.shareholder_holdings"],
  market: ["market_data.quotes (external governed market feed)"],
  events: ["ari_source.fact_observations", "ari_source.report_sections"],
  reports: ["ari_source.report_sections", "ari_source.section_classifications", "ari_source.report_documents"],
  guidance: ["ari_source.estimates_and_guidance"],
  research: ["ari_source.field_dictionary", "ari_source.schema_evolution_log"],
  evidence: ["ari.source_rows", "ari.source_cells", "ari.lineage_edges"],
};
// present-data feeds override for AVAILABLE/PARTIAL modules
const PRESENT_TABLES = {
  financial_statements: ["ari.statement_instances", "ari.source_rows", "ari.source_cells"],
  full_report_explorer: ["ari.source_rows", "ari.source_cells", "ari.structural_eligibility"],
  source_evidence: ["ari.lineage_edges", "ari.source_atoms", "ari.evidence_artifacts"],
  data_dictionary: ["ari (contract metadata)", "ari.*"],
  data_access: ["ari (preflight)", "ari_source (availability)"],
  review_quality: ["ari.certification_runs", "ari.pipeline_stage_runs", "ari.structural_eligibility"],
  company_overview: ["ari.report_instances", "ari.statement_instances", "ari.source_rows"],
  annual_reports: ["ari.report_instances", "ari.statement_instances"],
};

// icon keys resolved in AriApp
const GROUPS = [
  { id: "company", label: "Company", icon: "Building2", domain: "company", children: [
    ["General", "ARI_PARTIAL", null, "company_overview"],
    ["Snapshot", "ARI_PARTIAL", null, "company_overview"],
    ["Background", "ARI_SOURCE"], ["History", "ARI_SOURCE"],
    ["Top Management", "ARI_SOURCE", null, "governance"], ["Top Executives", "ARI_SOURCE", null, "governance"],
    ["Bankers", "ARI_SOURCE"], ["Locations and Addresses", "ARI_SOURCE"], ["Collaborators", "ARI_SOURCE"],
    ["Credit Ratings", "ARI_SOURCE"], ["Auditors", "ARI_SOURCE", null, "audit"], ["Change of Name", "ARI_SOURCE"],
    ["Industry/NIC Classification", "ARI_SOURCE"], ["Subsidiaries", "ARI_SOURCE"], ["Related Parties", "ARI_SOURCE"],
  ]},
  { id: "financials", label: "Financial Statements", icon: "FileBarChart2", domain: "financials", children: [
    ["Company at a Glance", "ARI_PARTIAL", null, "company_overview"],
    ["Financial Overview", "ARI_PARTIAL", null, "financial_statements"],
    ["Balance Sheet", "ARI_PRESENT", null, "financial_statements"],
    ["Consolidated Balance Sheet", "ARI_PARTIAL", null, "financial_statements"],
    ["Balance Sheet Abstract", "ARI_SOURCE"],
    ["Profit & Loss", "ARI_SOURCE", null, "financial_statements"],
    ["Consolidated Profit & Loss", "ARI_SOURCE", null, "financial_statements"],
    ["Cash Flow", "ARI_SOURCE", null, "financial_statements"],
    ["Consolidated Cash Flow", "ARI_SOURCE", null, "financial_statements"],
    ["Changes in Equity", "ARI_SOURCE", null, "financial_statements"],
    ["Investments", "ARI_SOURCE"], ["Deferred Tax", "ARI_SOURCE"], ["Consolidated Deferred Tax", "ARI_SOURCE"],
    ["Segment Financials", "ARI_SOURCE"], ["Consolidated Segment Financials", "ARI_SOURCE"],
    ["Maturity Pattern", "ARI_SOURCE", ["BANKING", "NBFC"]], ["Funds Flow", "ARI_SOURCE"],
    ["Aggregated Financials", "ARI_SOURCE"], ["Revised Financial Statements", "ARI_SOURCE"],
    ["Notes and Schedules", "ARI_PARTIAL", null, "full_report_explorer"], ["Forex Data", "ARI_SOURCE"],
    ["R&D", "ARI_SOURCE"], ["Power and Fuel Consumption", "ARI_SOURCE", ["MANUFACTURING"]],
    ["Raw Materials", "ARI_SOURCE", ["MANUFACTURING"]], ["Finished Products", "ARI_SOURCE", ["MANUFACTURING"]],
  ]},
  { id: "ratios", label: "Ratios & Regulatory", icon: "Percent", domain: "ratios", children: [
    ["Key Financial Ratios", "ARI_SOURCE"], ["Consolidated Key Ratios", "ARI_SOURCE"],
    ["Valuation Ratios", "ARI_SOURCE"], ["TTM Valuation Ratios", "ARI_SOURCE"], ["DuPont Model", "ARI_SOURCE"],
    ["RBI/NHB Disclosures", "ARI_SOURCE", ["BANKING", "NBFC"]], ["Basel Disclosures", "ARI_SOURCE", ["BANKING"]],
    ["Tier-I Capital", "ARI_SOURCE", ["BANKING", "NBFC"]], ["CRAR", "ARI_SOURCE", ["BANKING", "NBFC"]],
    ["Gross Credit-Risk Exposure", "ARI_SOURCE", ["BANKING", "NBFC"]], ["Industry-wise Exposure", "ARI_SOURCE", ["BANKING", "NBFC"]],
    ["Risk-Weighted Exposure", "ARI_SOURCE", ["BANKING", "NBFC"]], ["AUM and ALM", "ARI_SOURCE", ["BANKING", "NBFC"]],
    ["GNPA and NNPA", "ARI_SOURCE", ["BANKING", "NBFC"]], ["Borrowing Profile", "ARI_SOURCE", ["BANKING", "NBFC"]],
  ]},
  { id: "working_results", label: "Working Results", icon: "CalendarClock", domain: "working_results", children: [
    ["Results at a Glance", "ARI_SOURCE"], ["Latest Results", "ARI_SOURCE"], ["Quarterly Reported", "ARI_SOURCE"],
    ["Quarterly Consolidated", "ARI_SOURCE"], ["Quarterly Trailing", "ARI_SOURCE"], ["Half-Yearly Reported", "ARI_SOURCE"],
    ["Half-Yearly Consolidated", "ARI_SOURCE"], ["Nine-Month Reported", "ARI_SOURCE"], ["Nine-Month Consolidated", "ARI_SOURCE"],
    ["Full-Year Reported", "ARI_SOURCE"], ["Full-Year Consolidated", "ARI_SOURCE"], ["Assets and Liabilities", "ARI_SOURCE"],
    ["Indexing/Comparison Views", "ARI_SOURCE"],
  ]},
  { id: "governance", label: "Governance & Management", icon: "Users", domain: "governance", children: [
    ["Directors", "ARI_SOURCE", null, "governance"], ["KMP", "ARI_SOURCE", null, "governance"],
    ["Board Committees", "ARI_SOURCE", null, "governance"], ["Committee Memberships", "ARI_SOURCE", null, "governance"],
    ["Appointments and Cessations", "ARI_SOURCE", null, "governance"], ["Director's Report", "ARI_SOURCE"],
    ["Chairman's Speech", "ARI_SOURCE"], ["Corporate Governance Report", "ARI_SOURCE"],
  ]},
  { id: "audit", label: "Audit", icon: "ShieldCheck", domain: "audit", children: [
    ["Statutory Auditors", "ARI_SOURCE", null, "audit"], ["Secretarial Auditors", "ARI_SOURCE", null, "audit"],
    ["Internal Auditors", "ARI_SOURCE", null, "audit"], ["Audit Opinion", "ARI_SOURCE", null, "audit"],
    ["Basis for Opinion", "ARI_SOURCE", null, "audit"], ["Key Audit Matters", "ARI_SOURCE", null, "audit"],
    ["Auditor's Report", "ARI_SOURCE", null, "audit"],
  ]},
  { id: "ownership", label: "Ownership & Equity", icon: "PieChart", domain: "ownership", children: [
    ["Shareholding Pattern", "ARI_SOURCE", null, "shareholding"], ["Promoter/Public Holding", "ARI_SOURCE", null, "shareholding"],
    ["Major Shareholders", "ARI_SOURCE", null, "shareholding"], ["Fund Holdings", "ARI_SOURCE", null, "shareholding"],
    ["Equity History", "ARI_SOURCE"], ["Bonus History", "ARI_SOURCE"], ["Dividend History", "ARI_SOURCE"],
    ["QIP", "ARI_SOURCE"], ["IPO Details", "ARI_SOURCE"],
  ]},
  { id: "market", label: "Share Price & Market Data", icon: "TrendingUp", domain: "market", children: [
    ["Latest Equity Data", "MARKET_DATA"], ["Share Price", "MARKET_DATA"], ["High/Low Price", "MARKET_DATA"],
    ["Stock Returns", "MARKET_DATA"], ["Beta Analysis", "MARKET_DATA"], ["Bulk Deals", "MARKET_DATA"],
    ["Block Deals", "MARKET_DATA"], ["Deliverable Volume", "MARKET_DATA"], ["Index Weightage", "MARKET_DATA"],
    ["Daily Ratios", "MARKET_DATA"], ["Total Shareholder Return", "MARKET_DATA"],
  ]},
  { id: "events", label: "Corporate Events", icon: "CalendarClock", domain: "events", children: [
    ["Dividends", "ARI_SOURCE"], ["Bonus", "ARI_SOURCE"], ["Splits", "ARI_SOURCE"], ["Rights", "ARI_SOURCE"],
    ["Buybacks", "ARI_SOURCE"], ["Mergers/Demergers", "ARI_SOURCE"], ["Acquisitions", "ARI_SOURCE"],
    ["Board Meetings", "ARI_SOURCE"], ["AGMs/EGMs", "ARI_SOURCE"], ["Other Corporate Actions", "ARI_SOURCE"],
  ]},
  { id: "reports", label: "Reports & Disclosures", icon: "FileText", domain: "reports", children: [
    ["Complete Annual Report", "ARI_PRESENT", null, "full_report_explorer"], ["Director's Report", "ARI_SOURCE"],
    ["Auditor's Report", "ARI_SOURCE", null, "audit"], ["Corporate Governance", "ARI_SOURCE"],
    ["Management Discussion and Analysis", "ARI_SOURCE"], ["Notes to Accounts", "ARI_PARTIAL", null, "full_report_explorer"],
    ["Sustainability/ESG", "ARI_SOURCE"], ["Regulatory Disclosures", "ARI_SOURCE"], ["Prospectus", "ARI_SOURCE"],
    ["Exchange Filings", "ARI_SOURCE"],
  ]},
  { id: "guidance", label: "Estimates & Guidance", icon: "Target", domain: "guidance", children: [
    ["Management Outlook", "ARI_SOURCE", null, "guidance"], ["Quantitative Guidance", "ARI_SOURCE", null, "guidance"],
    ["Qualitative Guidance", "ARI_SOURCE", null, "guidance"], ["Targets", "ARI_SOURCE", null, "guidance"],
    ["Capital Expenditure Plans", "ARI_SOURCE", null, "guidance"], ["Expansion Plans", "ARI_SOURCE", null, "guidance"],
    ["Management Estimates", "ARI_SOURCE", null, "guidance"], ["AI-Derived Observations", "ARI_SOURCE", null, "guidance"],
  ]},
  { id: "research", label: "Research & Comparison", icon: "GitCompare", domain: "research", children: [
    ["Peer Set", "ARI_SOURCE"], ["Company Comparison", "ARI_SOURCE"], ["Industry Comparison", "ARI_SOURCE"],
    ["Historical Trends", "ARI_PARTIAL", null, "financial_statements"], ["Financial Screener", "ARI_SOURCE"],
    ["New Findings", "ARI_SOURCE", null, "data_dictionary", "admin"], ["Schema-Evolution Candidates", "ARI_SOURCE", null, "data_dictionary", "admin"],
  ]},
  { id: "evidence", label: "Full Report & Evidence", icon: "GitBranch", domain: "evidence", children: [
    ["Full Report Explorer", "ARI_PRESENT", null, "full_report_explorer"], ["Page Search", "ARI_PRESENT", null, "full_report_explorer"],
    ["Source Evidence", "ARI_PRESENT", null, "source_evidence"], ["Lineage", "ARI_PRESENT", null, "source_evidence", "admin"],
    ["Extraction Status", "ARI_PARTIAL", null, "review_quality", "admin"], ["Review Queue", "ARI_PARTIAL", null, "review_quality", "admin"],
    ["Data Dictionary", "ARI_PRESENT", null, "data_dictionary", "admin"], ["Data Access/Preflight", "ARI_PRESENT", null, "data_access", "admin"],
  ]},
];

const slug = (s) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export function buildTaxonomy() {
  const flat = [];
  GROUPS.forEach((g, gi) => {
    flat.push({ menu_id: g.id, parent_menu_id: null, label: g.label, icon: g.icon,
      business_domain: g.domain, industry_applicability: ["ALL"], source_tables: [],
      availability_rule: "GROUP", display_order: (gi + 1) * 100, isGroup: true });
    g.children.forEach((c, ci) => {
      const [label, rule, industry, render, area] = c;
      const id = `${g.id}__${slug(label)}`;
      const tables = (render && PRESENT_TABLES[render]) ? PRESENT_TABLES[render] : DOMAIN_TABLES[g.domain] || [];
      flat.push({
        menu_id: id, parent_menu_id: g.id, label, icon: g.icon,
        business_domain: g.domain, industry_applicability: industry || ["ALL"],
        source_tables: tables, availability_rule: rule, render: render || null,
        area: area || "analyst", display_order: (gi + 1) * 100 + (ci + 1),
      });
    });
  });
  return flat;
}

export const GROUP_META = GROUPS.map((g) => ({ id: g.id, label: g.label, icon: g.icon, domain: g.domain }));

// company classification (navigation metadata; overridden by governed company_master when delivered)
export function classifyCompany(name) {
  const n = (name || "").toLowerCase();
  if (/\bbank\b/.test(n)) return { industry: "Financials", subIndustry: "Banks", tags: ["BANKING"], known: true };
  if (/(nbfc|non-banking)/.test(n)) return { industry: "Financials", subIndustry: "NBFC", tags: ["NBFC"], known: true };
  if (/(finance|financial|capital|investment|housing fin|infrastructure)/.test(n)) return { industry: "Financials", subIndustry: "NBFC / Investment", tags: ["NBFC"], known: true };
  return { industry: "Unclassified", subIndustry: "", tags: [], known: false };
}

export const INDUSTRY_OPTIONS = [
  { id: "AUTO", label: "Auto (from company)" },
  { id: "BANKING", label: "Banking" },
  { id: "NBFC", label: "NBFC / Financial services" },
  { id: "MANUFACTURING", label: "Manufacturing" },
  { id: "GENERAL", label: "General / Other" },
];

export function resolveAvailability(item, ctx) {
  const ind = item.industry_applicability || ["ALL"];
  if (!ind.includes("ALL")) {
    const match = ind.some((t) => ctx.industryTags.includes(t));
    if (!match) return ctx.industryKnown ? "NOT_APPLICABLE" : "AWAITING_GOVERNED_SOURCE";
  }
  return RULE_STATE[item.availability_rule] || "AWAITING_GOVERNED_SOURCE";
}

export const AVAILABILITY_STATES = ["AVAILABLE", "PARTIAL", "AWAITING_GOVERNED_SOURCE", "NOT_APPLICABLE"];
export const AVAIL_RANK = { AVAILABLE: 0, PARTIAL: 1, AWAITING_GOVERNED_SOURCE: 2, NOT_APPLICABLE: 3 };

// Seven analyst-facing principal work areas mapped over the 146-module taxonomy.
// This is a PRESENTATION layer only — no module is renamed, deleted or reclassified.
export const AREAS = [
  { id: "cockpit", label: "Cockpit", icon: "LayoutDashboard", index: true },
  { id: "company", label: "Company", icon: "Building2" },
  { id: "financials", label: "Financials", icon: "FileBarChart2" },
  { id: "performance", label: "Performance & Regulatory", icon: "Percent" },
  { id: "governance", label: "Governance & Ownership", icon: "Users" },
  { id: "reports", label: "Reports & Disclosures", icon: "FileText" },
  { id: "data", label: "Data & Evidence", icon: "GitBranch" },
];
const DOMAIN_AREA = {
  company: "company", events: "company",
  financials: "financials",
  ratios: "performance", working_results: "performance", market: "performance",
  governance: "governance", audit: "governance", ownership: "governance",
  reports: "reports", guidance: "reports",
  evidence: "data",
};
export function areaOf(item) {
  if (item.business_domain === "research") return isAdmin(item) ? "data" : "performance";
  return DOMAIN_AREA[item.business_domain] || "data";
}
export function itemsForArea(taxonomy, areaId) {
  return taxonomy.filter((t) => !t.isGroup && areaOf(t) === areaId);
}
export const AREA_GUIDANCE = {
  company: "Identity, management, business & products and corporate events for the selected company.",
  financials: "Primary financial statements and disclosures as reported, with year-on-year comparison and source drill-down.",
  performance: "Ratios, periodic working results, peer comparison and — for banks/NBFCs — regulatory capital and asset-quality disclosures.",
  governance: "Directors, KMP, board committees, auditors, key audit matters and shareholding ownership.",
  reports: "The complete annual report, regulatory disclosures, management guidance and prospectus material.",
  data: "Source evidence, lineage and data-quality controls behind every governed fact.",
};

export const AVAILABILITY_STATES_LEGACY = AVAILABILITY_STATES;


// SINGLE canonical taxonomy/status computation. Cockpit, directory, coverage panel
// and the preflight surface all derive their counts from this — never hard-coded.
export function computeStatus(taxonomy, ctx) {
  const items = taxonomy.filter((t) => !t.isGroup);
  const byState = { AVAILABLE: 0, PARTIAL: 0, AWAITING_GOVERNED_SOURCE: 0, NOT_APPLICABLE: 0 };
  const byDomain = {};
  items.forEach((t) => {
    const s = resolveAvailability(t, ctx);
    byState[s] += 1;
    const d = (byDomain[t.business_domain] = byDomain[t.business_domain] || { AVAILABLE: 0, PARTIAL: 0, AWAITING_GOVERNED_SOURCE: 0, NOT_APPLICABLE: 0, total: 0 });
    d[s] += 1; d.total += 1;
  });
  const total = items.length;
  const applicable = total - byState.NOT_APPLICABLE; // applicable excludes ONLY not-applicable
  const covered = byState.AVAILABLE + byState.PARTIAL;
  const pct = applicable ? Math.round((covered / applicable) * 100) : 0;
  return { total, byState, byDomain, applicable, covered, pct };
}

export const isAdmin = (item) => item.area === "admin";

// business-first guidance for awaiting-source modules (analyst language; no SQL leading)
export const BUSINESS_GUIDANCE = {
  company: "Company identity — legal name, registered & corporate addresses, identifiers (CIN / PAN / LEI / ISIN), group structure and history.",
  financials: "Financial statements and disclosures exactly as reported in the annual report, with year-on-year comparison and source drill-down.",
  ratios: "Financial ratios and, for banks/NBFCs, regulatory capital and asset-quality disclosures.",
  working_results: "Periodic results — quarterly, half-yearly and full-year, reported and consolidated.",
  governance: "Directors, key managerial personnel and board committees — unique resolved people with roles, tenure and memberships.",
  audit: "Auditors, audit opinions and Key Audit Matters, each backed by exact source evidence.",
  ownership: "Shareholding pattern, promoter/public split, major holders and equity history.",
  market: "Market data such as price, returns and deals. This requires a governed market-data feed — annual-report figures are never presented as live market data.",
  events: "Corporate actions — dividends, bonuses, splits, rights, buybacks, mergers and meetings.",
  reports: "Report sections and disclosures — Director's and Auditor's reports, corporate governance, MD&A, ESG and filings.",
  guidance: "Management outlook, targets and guidance, kept visibly separate from audited actuals and AI-derived observations.",
  research: "Peer and industry comparison, historical trends and newly discovered fields.",
  evidence: "Source evidence, lineage and data-quality controls behind every governed fact.",
};

export function moduleGuidance(item) {
  const domain = BUSINESS_GUIDANCE[item.business_domain] || "This module presents governed disclosures from the annual report.";
  const expected = item.source_tables.some((t) => String(t).startsWith("ari_source"))
    ? `Governed ari_source ${item.business_domain} schema`
    : item.source_tables.some((t) => String(t).startsWith("market"))
      ? "Governed market-data source"
      : `Governed ${item.business_domain} source`;
  return {
    will: `“${item.label}” will show: ${domain}`,
    status: "Awaiting governed source — not yet ingested",
    why: "The governed source for this information has not yet been delivered to the studio. Nothing is fabricated in the meantime.",
    expected,
  };
}
